/**
 * Notices that a newer build of the app has been deployed, and reloads (idle screens) or offers a reload (reading screens).
 * The rules live in updateSafety.ts and are tested there; this file only does the fetching, the timers and the bar.
 */

import {
  UPDATE_CHECK_EVERY_MS,
  UPDATE_FIRST_CHECK_MS,
  UPDATE_SAFE_ATTR,
  decideUpdate,
  isNewerBuild,
  parseBuild,
  type UpdateSafety,
} from './updateSafety';

const RELOADED_FOR_KEY = 'mahjong-scorer:updateReloadedFor';
const BAR_ID = 'updateBar';

let latestBuild: string | undefined;
let lastTouchAt = Date.now();
let watching = false;
let tick: ReturnType<typeof setInterval> | undefined;

/** The newest deployed build's time, or undefined when it cannot be read (offline, dev server, a bad answer) — then nothing happens. */
async function fetchLatestBuild(): Promise<string | undefined> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return undefined;
    return parseBuild(await res.json());
  } catch {
    return undefined;
  }
}

function currentSafety(): UpdateSafety | undefined {
  const value = document.querySelector(`[${UPDATE_SAFE_ATTR}]`)?.getAttribute(UPDATE_SAFE_ATTR);
  return value === 'idle' || value === 'ok' ? value : undefined;
}

function alreadyReloadedFor(build: string | undefined): boolean {
  try {
    return build !== undefined && sessionStorage.getItem(RELOADED_FOR_KEY) === build;
  } catch {
    return true; // cannot remember it did, so never risk a reload loop
  }
}

function showBar(): void {
  if (document.getElementById(BAR_ID)) return;
  const bar = document.createElement('div');
  bar.id = BAR_ID;
  bar.className = 'update-bar';
  bar.setAttribute('role', 'status');
  bar.innerHTML = `<span>A new version of the app is available.</span><button type="button" id="updateReloadBtn">Reload</button>`;
  document.body.appendChild(bar);
  bar.querySelector('#updateReloadBtn')!.addEventListener('click', () => location.reload());
}

function hideBar(): void {
  document.getElementById(BAR_ID)?.remove();
}

function evaluate(): void {
  const safety = currentSafety();
  if (safety === undefined) {
    hideBar(); // the player moved on to something in progress: no prompt there
    return;
  }
  const action = decideUpdate({
    newer: isNewerBuild(__BUILD_TIME__, latestBuild),
    safety,
    quietMs: Date.now() - lastTouchAt,
    alreadyReloaded: alreadyReloadedFor(latestBuild),
  });
  if (action === 'bar') showBar();
  if (action === 'reload') {
    try {
      sessionStorage.setItem(RELOADED_FOR_KEY, latestBuild ?? '');
    } catch {
      // alreadyReloadedFor() answers "yes" when storage is unavailable, so this branch is not reached then.
    }
    location.reload();
  }
}

async function check(): Promise<void> {
  if (document.hidden) return;
  const latest = await fetchLatestBuild();
  if (latest === undefined) return;
  latestBuild = latest;
  if (!isNewerBuild(__BUILD_TIME__, latestBuild)) return;
  evaluate();
  // Once a newer build is known, keep looking at the screen (it changes, and the player goes quiet) until it has been dealt with.
  if (tick === undefined) tick = setInterval(evaluate, 1000);
}

/** Starts watching. Call once, from `mount`, for every kind of screen (player, guest, admin, the venue TV). */
export function startUpdateWatch(): void {
  if (watching) return;
  watching = true;
  const touch = (): void => {
    lastTouchAt = Date.now();
  };
  for (const type of ['pointerdown', 'keydown', 'input', 'touchstart', 'scroll', 'wheel']) {
    document.addEventListener(type, touch, { capture: true, passive: true });
  }
  setTimeout(() => void check(), UPDATE_FIRST_CHECK_MS);
  setInterval(() => void check(), UPDATE_CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void check();
  });
}
